import { useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "../redux/store/hooks"
import { selectDashboardSelectedPatient } from "../redux/ProfessionalDashboard"
import {
    clearOpenAIKey,
    selectOpenAIKey,
    setOpenAIKey
} from "../redux/AccountSettings"
import DashboardWrapper from "../component/DashboardWrapper"
import SmallHeadingComponent from "../component/SmallHeadingComponent"
import Card from "../component/Card"
import {
    GetPatientsOfProfessionalResponsePayload,
    UpdatePatientLongtermSettingsSpecification
} from "pokal_api"
import { ArrayElement } from "../types/ArrayElement"
import { NAVIGATION_VIEW, NavigationPaths } from "../navigation/NavigationController"
import { useNavigate } from "react-router-dom"

function maskKey(key: string): string {
    if (key.length <= 8) return "••••"
    return `${key.slice(0, 3)}••••${key.slice(-4)}`
}

function PatientSettingsView() {
    const fetchedPatient = useAppSelector(selectDashboardSelectedPatient)
    const openaiKey = useAppSelector(selectOpenAIKey)
    const dispatch = useAppDispatch()

    const [patient, setPatient] =
        useState<ArrayElement<GetPatientsOfProfessionalResponsePayload> | null>(null)
    const [loading, setLoading] = useState(false)
    const [keyInput, setKeyInput] = useState("")
    const navigate = useNavigate()

    useEffect(() => {
        setPatient(fetchedPatient)
    }, [fetchedPatient])

    function handleToggle() {
        if (!patient) return
        const next = !patient.hideLongtermData
        setLoading(true)
        UpdatePatientLongtermSettingsSpecification.request({
            patientId: patient.id,
            hideLongtermData: next
        })
            .then(() => {
                setPatient({ ...patient, hideLongtermData: next })
            })
            .finally(() => setLoading(false))
    }

    function handleSaveKey() {
        const trimmed = keyInput.trim()
        if (!trimmed) return
        dispatch(setOpenAIKey(trimmed))
        setKeyInput("")
    }

    function handleClearKey() {
        dispatch(clearOpenAIKey())
    }

    return (
        <DashboardWrapper
            title={patient ? `${patient.firstName} ${patient.lastName}` : "Einstellungen"}
            onClick={() =>
                navigate(NavigationPaths[NAVIGATION_VIEW.PATIENT_OVERVIEW].path)
            }>
            <div className="flex flex-col gap-6">
                {patient && (
                    <>
                        <SmallHeadingComponent>Patient:innen-Einstellungen</SmallHeadingComponent>
                        <Card>
                            <div className="flex items-center justify-between gap-6 py-2">
                                <div>
                                    <p className="font-semibold text-sm">Langzeiteinsicht</p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Gibt dem Patienten / der Patientin Zugang zu 14-Tage-Verlaufsdaten in der App.
                                    </p>
                                </div>
                                <button
                                    disabled={loading || !patient}
                                    onClick={handleToggle}
                                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                                        patient && !patient.hideLongtermData
                                            ? "bg-teal-500"
                                            : "bg-gray-300"
                                    } ${loading ? "opacity-50" : ""}`}>
                                    <span
                                        className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${
                                            patient && !patient.hideLongtermData
                                                ? "translate-x-5"
                                                : "translate-x-0"
                                        }`}
                                    />
                                </button>
                            </div>
                        </Card>
                    </>
                )}

                <SmallHeadingComponent>KI-Einstellungen</SmallHeadingComponent>
                <Card>
                    <div className="flex flex-col gap-3 py-2">
                        <div>
                            <p className="font-semibold text-sm">OpenAI API-Schlüssel</p>
                            <p className="text-xs text-gray-500 mt-1">
                                Aktivieren Sie OpenAI-Modelle (z. B. gpt-4o) im Chat. Der Schlüssel wird nur für diese Sitzung im Browser gespeichert (nicht dauerhaft, z. B. bei einem Neuladen der Seite verloren) und niemals in der Datenbank gespeichert. Er wird bei jeder Chat-Anfrage an den Server übermittelt, um die Anfrage an OpenAI weiterzuleiten.
                            </p>
                        </div>
                        {openaiKey !== null ? (
                            <div className="flex items-center gap-3">
                                <div className="font-mono text-sm bg-gray-50 border border-gray-200 rounded px-3 py-1.5 text-gray-700">
                                    {maskKey(openaiKey)}
                                </div>
                                <button
                                    type="button"
                                    onClick={handleClearKey}
                                    className="text-sm text-red-600 hover:text-red-700 font-medium"
                                >
                                    Entfernen
                                </button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">
                                <input
                                    type="password"
                                    value={keyInput}
                                    onChange={e => setKeyInput(e.target.value)}
                                    placeholder="sk-…"
                                    className="flex-1 rounded border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                />
                                <button
                                    type="button"
                                    onClick={handleSaveKey}
                                    disabled={!keyInput.trim()}
                                    className="rounded bg-teal-500 px-4 py-1.5 text-sm font-medium text-white hover:bg-teal-600 disabled:opacity-50"
                                >
                                    Übernehmen
                                </button>
                            </div>
                        )}
                    </div>
                </Card>
            </div>
        </DashboardWrapper>
    )
}

export default PatientSettingsView
